import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, Select } from "antd";
import { fetchInstituteList } from "./api";

interface InstituteOption {
  instituteId?: string;
  instituteName?: string;
  id?: string | number;
  name?: string;
}

interface UnitFieldProps {
  form: any;
  mode: "expert" | "user";
  value?: any;
  onChange?: (value: any) => void;
  placeholder?: string;
}

const getListFromResponse = (data: any): InstituteOption[] => {
  const source = data?.data ?? data;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.list)) return source.list;
  if (Array.isArray(source?.rows)) return source.rows;
  if (Array.isArray(source?.instituteList)) return source.instituteList;
  return [];
};

export default function UnitField({
  form,
  mode,
  value,
  onChange,
  placeholder = "请选择所在单位",
}: UnitFieldProps) {
  const [instituteList, setInstituteList] = useState<InstituteOption[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [manualUnit, setManualUnit] = useState("");
  const searchTimer = useRef<any>(null);

  const loadInstituteList = useCallback((instituteName?: string, isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setSearchLoading(true);
    }
    fetchInstituteList(instituteName ? { instituteName } : {})
      .then((res) => {
        setInstituteList(getListFromResponse(res));
      })
      .catch(() => {
        setInstituteList([]);
      })
      .finally(() => {
        if (isInitial) {
          setInitialLoading(false);
        } else {
          setSearchLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    loadInstituteList(undefined, true);
    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    };
  }, [loadInstituteList]);

  const options = useMemo(() => instituteList
    .map((item) => ({
      value: item.instituteId || String(item.id || ""),
      label: item.instituteName || item.name || "",
    }))
    .filter((item) => item.value && item.label), [instituteList]);

  useEffect(() => {
    if (mode === "expert" && !initialLoading && options.length === 0) {
      setManualUnit(form.getFieldValue("unit") || "");
    }
  }, [form, initialLoading, mode, options.length]);

  const expertUnitField = mode === "expert" ? (
    <Form.Item name="unit" noStyle>
      <Input type="hidden" />
    </Form.Item>
  ) : null;

  const handleSearch = (keyword: string) => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchTimer.current = setTimeout(() => {
      loadInstituteList(keyword);
    }, 300);
  };

  if (initialLoading) {
    return (
      <>
        <Select
          loading
          disabled
          value={value}
          placeholder={placeholder}
        />
        {expertUnitField}
      </>
    );
  }

  if (options.length === 0) {
    return (
      <>
        <Input
          value={mode === "expert" ? manualUnit : value}
          placeholder="请输入所在单位"
          onChange={(event) => {
            const nextValue = event.target.value;
            if (mode === "expert") {
              setManualUnit(nextValue);
              form.setFieldsValue({ unit: nextValue, unitInstituteId: undefined });
              onChange?.(undefined);
              return;
            }
            onChange?.(nextValue);
          }}
        />
        {expertUnitField}
      </>
    );
  }

  if (mode === "expert") {
    return (
      <>
        <Select
          allowClear
          showSearch
          value={value}
          loading={searchLoading}
          optionFilterProp="children"
          filterOption={false}
          placeholder={placeholder}
          onSearch={handleSearch}
          onChange={(value, option: any) => {
            const unit = value ? option?.children : undefined;
            form.setFieldsValue({ unit });
            onChange?.(value);
          }}
        >
          {options.map((item) => (
            <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
          ))}
        </Select>
        {expertUnitField}
      </>
    );
  }

  return (
    <Select
      allowClear
      showSearch
      value={value}
      loading={searchLoading}
      optionFilterProp="children"
      filterOption={false}
      placeholder={placeholder}
      onSearch={handleSearch}
      onChange={onChange}
    >
      {options.map((item) => (
        <Select.Option key={item.value} value={item.label}>{item.label}</Select.Option>
      ))}
    </Select>
  );
}
